import { type ClassValue, clsx } from 'clsx';
import { extendTailwindMerge } from 'tailwind-merge';

/**
 * Custom tailwind-merge instance that teaches twMerge ALL of our semantic
 * design-system token utilities, so conflicting tokens reconcile correctly
 * inside `cn()` (last-one-wins per group) and non-conflicting tokens are
 * never dropped.
 *
 * Without these entries twMerge cannot see our tokens — two failure modes:
 *
 *   1. Same-prefix ambiguity — `text-emphasis4` (font-size) lands in the
 *      same `text-*` bucket as `text-state-info` (colour) and one is
 *      silently dropped. Original SoftBadge bug: `text-emphasis4` (size,
 *      cva base) was stripped in favour of `text-state-info` (colour, cva
 *      variant), so the badge inherited the parent font-size (rendered
 *      ~14-16px instead of 11px).
 *
 *   2. Unknown custom values — `h-btn-md`, `px-card`, `gap-inline` were not
 *      recognised as height / padding / gap, so when a consumer merged them
 *      with built-ins they collapsed at runtime. That forced PillButton to
 *      fall back to raw `h-8/px-4/text-xs`. Registering the tokens makes them
 *      first-class, so primitives consume the design system instead of
 *      bypassing it (PR-DS-A).
 *
 * Keep this list in sync with `tailwind-preset.ts` (the class shapes) and
 * `styles/tokens.css` (the values).
 *
 * Verify behaviour:
 *   cn('h-10', 'h-btn-sm')                  // → 'h-btn-sm'  (token wins)
 *   cn('text-emphasis4', 'text-state-info') // → both kept (size + colour)
 */
const SPACING_TOKENS = [
  'page',
  'section',
  'card',
  'card-sm',
  'card-stack',
  'row',
  'inline',
  'control',
];

const twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      'font-size': [
        {
          text: [
            'display1',
            'heading1', 'heading2', 'heading3',
            'body1', 'body2', 'body3',
            'emphasis1', 'emphasis2', 'emphasis3', 'emphasis4',
          ],
        },
      ],
      rounded: [{ rounded: ['xs', 'chip', 'inner', 'card', 'widget'] }],
      h: [{ h: ['btn-sm', 'btn-md', 'btn-lg', 'control'] }],
      'max-h': [{ 'max-h': ['table-sticky'] }],
      'max-w': [{ 'max-w': ['focused', 'focused-toolbar'] }],
      shadow: [{ shadow: ['glass'] }],
      'backdrop-blur': [{ 'backdrop-blur': ['glass'] }],
      z: [{ z: ['dropdown', 'sticky', 'overlay', 'modal', 'popover', 'toast'] }],
      // Semantic spacing tokens apply to every padding / margin / gap / space group.
      p: [{ p: SPACING_TOKENS }],
      px: [{ px: SPACING_TOKENS }],
      py: [{ py: SPACING_TOKENS }],
      pt: [{ pt: SPACING_TOKENS }],
      pr: [{ pr: SPACING_TOKENS }],
      pb: [{ pb: SPACING_TOKENS }],
      pl: [{ pl: SPACING_TOKENS }],
      m: [{ m: SPACING_TOKENS }],
      mx: [{ mx: SPACING_TOKENS }],
      my: [{ my: SPACING_TOKENS }],
      mt: [{ mt: SPACING_TOKENS }],
      mr: [{ mr: SPACING_TOKENS }],
      mb: [{ mb: SPACING_TOKENS }],
      ml: [{ ml: SPACING_TOKENS }],
      gap: [{ gap: SPACING_TOKENS }],
      'gap-x': [{ 'gap-x': SPACING_TOKENS }],
      'gap-y': [{ 'gap-y': SPACING_TOKENS }],
      'space-x': [{ 'space-x': SPACING_TOKENS }],
      'space-y': [{ 'space-y': SPACING_TOKENS }],
    },
  },
});

export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}
